import { App as CapApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Dialog } from '@capacitor/dialog';
import { supabase } from "@/lib/supabase";
import { establishClientSession, enterAuthenticatedApp } from "@/lib/authSync";
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { queryClient } from "@/lib/queryClient";

let isProcessing = false;
const LAST_PROCESSED_KEY = 'last_processed_auth_url';

const PENDING_AUTH_KEY = 'pending_google_auth';

/** Backend session already usable — do not re-run OAuth sync or scare the user with Login Failed. */
function hasActiveBackendSession(): boolean {
  try {
    const token = localStorage.getItem('sessionToken');
    if (!token) return false;
    const user = queryClient.getQueryData(['/api/auth/user']);
    return !!user;
  } catch {
    return false;
  }
}

// Global Deep Link Handler to safely capture events even if React isn't ready
export function initDeepLinkHandler() {
  console.log('🔌 Deep Link Handler Initialized - v5 (Store & Reload)');
  
  // 🟢 CHECK FOR PENDING AUTH ON BOOT
  // This runs when the page reloads after we captured the token
  checkPendingAuth();

  // 0. 🛡️ Global Function for Native "Push" Mechanism
  (window as any).handleCapacitorDeepLink = async (url: string) => {
      console.log('🔌 Native Direct Call received:', url);
      processAuthUrl(url);
  };
  
  // 1. Standard Capacitor Event Listener (Might fail if Bridge is broken)
  CapApp.addListener('appUrlOpen', async (data) => {
    console.log('🔌 Deep Link Received (Event):', JSON.stringify(data));
    try { await Browser.close(); } catch (e) {}
    if (data.url) await processAuthUrl(data.url);
  });

  // 2. Capacitor Resume Fallback
  CapApp.addListener('appStateChange', async (state) => {
    if (state.isActive) {
      console.log('🔌 App Resumed (Capacitor) - Checking...');
      await checkFallbackLocations();
      // Start polling on Resume
      startPolling();
    }
  });

  // 3. 🛡️ DOM Visibility Fallback (Bypasses Capacitor Bridge)
  // This triggers when the Safari View Controller is dismissed, revealing the WebView
  document.addEventListener('visibilitychange', async () => {
    // DEBUG: Alert to confirm JS is alive
    console.log(`🔌 Visibility changed to: ${document.visibilityState}`);
    if (document.visibilityState === 'visible') {
       // Optional: Uncomment to debug if event fires
       // await Dialog.alert({ title: 'Debug', message: 'JS is Waking Up (Visibility)' }); 
       
       await checkFallbackLocations();
       startPolling();
    }
  });

  // 4. 🛡️ Window Focus Fallback
  window.addEventListener('focus', async () => {
    console.log('🔌 Window Focus - Checking...');
     // Optional: Uncomment to debug if event fires
     // await Dialog.alert({ title: 'Debug', message: 'JS is Waking Up (Focus)' });

    await checkFallbackLocations();
    // Start polling on Focus
    startPolling();
  });

  // 5. Initial Poll on Load (Just in case we are already visible)
  startPolling();
}

// 🛡️ Polling Mechanism
// Sometimes the Native Injection happens *milliseconds* after the visibility/focus event.
// Events fire immediately when the browser closes, but the Native 'evaluateJavaScript' might queue up.
function startPolling() {
    let attempts = 0;
    const maxAttempts = 120; // Try for 60 seconds (120 * 500ms) - Extended to handle slow redirects
    
    // Clear any existing interval to avoid pile-up
    if ((window as any).authPollInterval) clearInterval((window as any).authPollInterval);

    (window as any).authPollInterval = setInterval(async () => {
        attempts++;
        const found = await checkFallbackLocations();
        
        if (found || attempts >= maxAttempts) {
            clearInterval((window as any).authPollInterval);
            if (found) console.log('🔌 Polling Successful!');
            else console.log('🔌 Polling Finished (No Token Found) after 60s');
        }
    }, 500); 
}

// Separate fallback checker to be called by multiple triggers
// Returns TRUE if something was found, FALSE otherwise
async function checkFallbackLocations(): Promise<boolean> {
    // Already in the app with a valid backend session — never re-run stale OAuth sync.
    if (hasActiveBackendSession()) {
      return false;
    }

    // A. Check Capacitor Last Url (Might fail if Bridge is broken)
    try {
      const launchUrl = await CapApp.getLaunchUrl();
      if (launchUrl && launchUrl.url && (launchUrl.url.includes('google-auth') || launchUrl.url.includes('access_token'))) {
         console.log('🔌 Found Launch URL:', launchUrl.url);
         processAuthUrl(launchUrl.url);
         return true;
      }
    } catch(e) {}

    // B. Check Native Storage (Bridge dependent)
    try {
        const { value } = await Preferences.get({ key: 'last_deep_link_url' });
        if (value) {
            console.log('🔌 Found Saved URL in Preferences:', value);
            await Preferences.remove({ key: 'last_deep_link_url' });
            processAuthUrl(value);
            return true;
        }
    } catch (e) {}

    // C. Check "Nuclear" LocalStorage Injection (Bridge Independent!)
    // This connects with the "injectDeepLinkIntoWebView" in AppDelegate.swift
    const injectedUrl = localStorage.getItem('deep_link_backup_url');
    if (injectedUrl) {
       console.log('🔌 Found Injected URL in LocalStorage (Nuclear Catch):', injectedUrl);
       
       // Use dialog to inform user we caught it
       try {
          // await Dialog.alert({ title: '☢️ Login Detected', message: 'Restoring session...' });
          // Note: Removing Alert to prevent blocking loop, just process it.
       } catch (e) {}

       localStorage.removeItem('deep_link_backup_url');
       processAuthUrl(injectedUrl);
       return true;
    }

    return false;
}

// 🛡️ NO-OP SANITIZER: Supabase handles Base64URL correctly in newer versions.
// We strictly pass the token as-is. Validating without modification.
// Note: We used to have diagnostic code here, but the Global Polyfill in index.html 
// now handles the atob compatibility issue transparently.
function sanitizeToken(token: string | null): string | null {
    if (!token) return null;
    // 1. Trim whitespace
    let clean = token.trim();
    // 2. Remove any trailing hash or query params if they got attached (common in some redirect flows)
    clean = clean.split('#')[0].split('?')[0];

    // 3. 🛡️ SUPER AGGRESSIVE REPAIR
    // If we have access_token=... prefix (which I saw in your logs!), STRIP IT!
    if (clean.includes('access_token=')) {
         clean = clean.split('access_token=')[1];
    }
    if (clean.includes('refresh_token=')) {
         clean = clean.split('refresh_token=')[1];
    }
    // And if there is more junk after ampersand (like &expires_at=...)
    clean = clean.split('&')[0];
    
    return clean;
}

// 🛡️ Safe Token Decoder Helper
function safeDecodeToken(token: string) {
    try {
        const parts = token.split('.');
        if (parts.length !== 3) return null;
        
        const payload = parts[1];
        // Replacements for Base64URL -> Base64
        let base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
        // Pad
        while (base64.length % 4) {
            base64 += '=';
        }
        
        // Try native decode to verify if it throws
        const decoded = atob(base64);
        return JSON.parse(decoded);
    } catch (e: any) {
        console.error('❌ Manual Token Verify Failed:', e.message);
        return null;
    }
}

// Unified Processor
async function processAuthUrl(url: string) {
    // 🛡️ REMOVE isProcessing LOCK
    // Rely solely on URL uniqueness for debounce
    // This prevents scenarios where a failed/stalled attempt blocks a valid retry
    
    console.log('🔌 DeepLink Handler Active. UA:', navigator.userAgent);
    
    // De-bounce: Check if we just processed this exact URL (to prevent double-fire from event + resume)
    const lastUrl = localStorage.getItem(LAST_PROCESSED_KEY);
    
    // Check if the URL is identical to the last one we processed
    if (lastUrl === url) {
        console.log('🔌 Skipping duplicate URL processing (Debounce Match)');
        return;
    }

    if (url.includes('access_token') || url.includes('refresh_token')) {
      console.log('🔌 Processing Auth URL content...');
      console.log('🔌 Link info:', url.substring(0, 50) + '...');
      
      // Set the lock IMMEDIATELY to win the race against other listeners
      localStorage.setItem(LAST_PROCESSED_KEY, url);
      
      try {
        const urlObj = new URL(url);
        const hashParams = new URLSearchParams(urlObj.hash.substring(1));
        const queryParams = urlObj.searchParams;

        const access_token_raw = hashParams.get('access_token') || queryParams.get('access_token');
        const refresh_token_raw = hashParams.get('refresh_token') || queryParams.get('refresh_token');

        // 🛡️ SANITIZATION: Use Nuclear Sanitizer
        const access_token = sanitizeToken(access_token_raw);
        const refresh_token = sanitizeToken(refresh_token_raw);

        if (access_token) {
           console.log('🛡️ Sanitized Access Token (First 20 chars):', access_token.substring(0, 20) + '...');
        }

        if (access_token && refresh_token) {
           
           // OPTIMIZATION: Process Immediately (No Reload) to ensure smooth UX
           console.log('🔌 [Optimization] Executing Immediate Login...');
           
           await handleLogin(access_token, refresh_token);
        }
      } catch (err: any) {
        console.error('🔌 Parse Error:', err);
        isProcessing = false; // Reset if parse fails
      }
      // Note: We don't clear isProcessing here if success, because we want to block until reload happens
    }
}

// 🟢 NEW FUNCTION: Runs on page load to process saved tokens
async function checkPendingAuth() {
    const stored = localStorage.getItem(PENDING_AUTH_KEY);
    // Clean up the reload flag regardless
    if (localStorage.getItem('HAS_RELOADED_FOR_AUTH')) {
         console.log('🔌 [Boot] Cleaned up reload flag');
         localStorage.removeItem('HAS_RELOADED_FOR_AUTH');
    }

    if (!stored) return;
    
    console.log('🔌 [Boot] Found Pending Auth from reload!');
    // Clear it immediately to prevent loops
    localStorage.removeItem(PENDING_AUTH_KEY);
    
    try {
        const payload = JSON.parse(stored);
        // 🛡️ Extra safety: ensure tokens are clean even if stored logic missed it
        const access_token = sanitizeToken(payload.access_token);
        const refresh_token = sanitizeToken(payload.refresh_token);

        if (access_token && refresh_token) {
            
            // 🛡️ NETWORK PROBE: ensuring we truly have connectivity before hitting Supabase
            // The WebView might report "loaded" but still be in a restricted network state after dismissal
            console.log('🔌 [Boot] Probing Network Connectivity...');
            let connected = false;
            for(let i=0; i<10; i++) {
                try {
                     // Simple fetch to a reliable light endpoint (Cloudflare Trace or similar, or just our Backend)
                     // Using a no-cors mode just to check if request *leaves* the device without error
                     await fetch('https://www.google.com/generate_204', { mode: 'no-cors', cache: 'no-store' });
                     connected = true;
                     console.log('🔌 [Boot] Network Probe Successful!');
                     break;
                } catch(e) {
                    console.log(`🔌 [Boot] Network Probe Failed (${i+1}/10), waiting...`);
                    await new Promise(r => setTimeout(r, 1000));
                }
            }

            if (!connected) {
                 console.error('🔌 [Boot] Critical: Network dead after reload. Cannot login.');
                 try { await Dialog.alert({ title: 'Network Error', message: 'Unable to connect to login server. Please restart the app.' }); } catch(e){}
                 return;
            }

            await handleLogin(access_token, refresh_token);
        }
    } catch(e) {
        console.error('🔌 Failed to parse pending auth', e);
    }
}

// Helper for robust retries
async function retryOperation<T>(operation: () => Promise<T>, retries = 3, delay = 1000, context = 'Operation'): Promise<T> {
    for (let i = 0; i < retries; i++) {
        try {
            return await operation();
        } catch (error: any) {
            console.log(`🔌 ${context} failed (Attempt ${i + 1}/${retries}): ${error.message || JSON.stringify(error)}`);
            if (i === retries - 1) throw error;
            await new Promise(resolve => setTimeout(resolve, delay));
        }
    }
    throw new Error('Unreachable');
}

async function manualSupabaseLocalStorageInjection(access_token: string, refresh_token: string) {
    try {
        console.log('💉 Initiating Manual Session Injection (Bypassing SDK)...');
        // 1. Get Project URL from Supabase Client (or Env)
        const projectUrl = import.meta.env.VITE_SUPABASE_URL;
        
        // 2. OFFLINE STRATEGY: Construct Session directly from JWT
        // We do this instead of fetching /user because the network might be restricted (WAF/WebView issues)
        console.log('💉 Decoding JWT to reconstruct User Session...');
        
        const decoded = safeDecodeToken(access_token);
        if (!decoded || !decoded.sub) {
            throw new Error('JWT Decode Failed or Invalid Token');
        }

        console.log('💉 JWT Decoded. User ID:', decoded.sub);

        // Reconstruct User Object from JWT Claims
        const user = {
            id: decoded.sub,
            aud: decoded.aud || 'authenticated',
            role: decoded.role || 'authenticated',
            email: decoded.email,
            email_confirmed_at: new Date(decoded.iat * 1000).toISOString(), // Approximate
            phone: decoded.phone || '',
            app_metadata: decoded.app_metadata || {},
            user_metadata: decoded.user_metadata || {},
            created_at: new Date(decoded.iat * 1000).toISOString(), // Approximate
            updated_at: new Date().toISOString()
        };

        const expires_at = decoded.exp || Math.floor(Date.now() / 1000) + 3600;
        
        const sessionData = {
            access_token,
            refresh_token,
            expires_at,
            expires_in: expires_at - Math.floor(Date.now() / 1000),
            token_type: 'bearer',
            user: user
        };

        // 3. Determine Storage Key
        // Helper: extract project ref from URL (https://xyz.supabase.co) -> xyz
        let projectRef = '';
        try {
            const hostname = new URL(projectUrl).hostname;
            // usually: ref.supabase.co
            projectRef = hostname.split('.')[0];
        } catch (e) {}
        
        // Supabase standard key format: sb-<REF>-auth-token
        const storageKey = `sb-${projectRef}-auth-token`;
        console.log(`💉 Injecting Session into Storage Key: ${storageKey}`);

        // 4. Save to LocalStorage
        localStorage.setItem(storageKey, JSON.stringify(sessionData));
        
        // 5. ALSO save backup for "Just in case"
        localStorage.setItem('supabase.auth.token', JSON.stringify(sessionData));

        console.log('💉 Injection Successful!');
        return true;

    } catch (e: any) {
        console.error('💉 Injection Failed:', e.message);
        return false;
    }
}

async function handleLogin(access_token: string, refresh_token: string) {
    // Already signed into the app — stale deep-link / launch URL must not re-login or alert.
    if (hasActiveBackendSession()) {
      console.log('🔌 [Login] Backend session already active — skipping OAuth re-sync');
      try {
        localStorage.removeItem('deep_link_backup_url');
        await Preferences.remove({ key: 'last_deep_link_url' });
      } catch {
        /* ignore */
      }
      return;
    }

    // ⏳ WAIT: Give the App time to fully resume from background before making network requests
    console.log('⏳ Waiting for App Resume/Network Stability (500ms)...');
    await new Promise(resolve => setTimeout(resolve, 500));

    // 🛡️ PRE-FLIGHT CHECK
    console.log('🛡️ Performing Pre-Flight Token Check...');
    const accessValid = safeDecodeToken(access_token);
    if (!accessValid) {
        // ... Log but try anyway
        console.error('⚠️ Local Decode Failed, but proceeding...');
    } else {
        console.log('✅ Access Token passed local decoding check.');
        // Expired OAuth access token from an old deep link — do not scare an already-usable day.
        if (typeof accessValid.exp === 'number' && accessValid.exp * 1000 < Date.now()) {
          console.warn('🔌 [Login] OAuth access token already expired — aborting silent re-sync');
          try {
            localStorage.removeItem('deep_link_backup_url');
            await Preferences.remove({ key: 'last_deep_link_url' });
          } catch {
            /* ignore */
          }
          return;
        }
    }
    
    const rtPreview = refresh_token.substring(0, 5) + '...';
    console.log(`🛡️ Refresh Token Preview: ${rtPreview} (Length: ${refresh_token.length})`);

    try {
        try { console.log('🔌 UI: Starting Login Sync...'); } catch(e) {}
    
        // A. Set Supabase Session (With Retry AND Manual Check)
        console.log(`🔌 Setting Supabase Session (Token length: ${access_token.length})...`);
        
        let sessionSetSuccess = false;

        // ATTEMPT 1: Standard SDK
        try {
             // ... SDK Logic ...
             const { error } = await supabase.auth.setSession({ access_token, refresh_token });
             if (error) throw error;
             sessionSetSuccess = true;
             console.log('🔌 Supabase SDK Session Set Successfully');
        } catch (sdkError: any) {
             console.error('❌ Supabase SDK SetSession Failed:', sdkError.message || sdkError);
             const isBase64Error = sdkError.name === 'DOMException' || (sdkError.message && sdkError.message.includes('pattern'));
             const isNetworkError = sdkError.status === 0 || sdkError.name === 'AuthRetryableFetchError' || (sdkError.message && sdkError.message.includes('Load failed'));
             
             if (isBase64Error || isNetworkError) {
                 console.warn(`⚠️ SDK Error detected (Network/Format). Failing over to MANUAL INJECTION...`);
                 // ATTEMPT 2: Manual Injection
                 sessionSetSuccess = await manualSupabaseLocalStorageInjection(access_token, refresh_token);
                 if (!sessionSetSuccess) throw new Error(`Both SDK and Manual Injection Failed. SDK Error: ${sdkError.message}`);
             } else {
                 throw sdkError; // Rethrow other errors (network, etc)
             }
        }

        // B. Sync with Backend (Only if we have a session locally)
        // ...
        
        const syncUrl = Capacitor.isNativePlatform() 
            ? 'https://moneytracker-backend-i77k.onrender.com/api/auth/login'
            : '/api/auth/login'; // Local / fallback

        const response = await retryOperation(
            () => fetch(syncUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token: access_token })
            }),
            3,
            1000,
            'Backend Sync'
        );

        if (!response.ok) {
            const errText = await response.text();
            throw new Error(`Sync failed: ${response.status} - ${errText}`);
        }

        const data = await response.json();
        
        // C. Finalize
        if (data.success && data.sessionToken) {
            console.log('🔌 [Login] Backend Sync Success. Updating Client State...');
            await establishClientSession(data.sessionToken);
            enterAuthenticatedApp();
            console.log('✅ Login Sequence Complete. Entering app...');
        } else {
            throw new Error('No Session Token in response');
        }
    } catch (err: any) {
        console.error('🔌 Login Logic Failed:', JSON.stringify(err));
        // Session already good (e.g. cold start race) — never show a false Login Failed.
        if (hasActiveBackendSession()) {
          console.warn('🔌 Login sync failed but backend session is active — suppressing alert');
          return;
        }
        try {
            await Dialog.alert({ title: '❌ Login Failed', message: err.message || 'Unknown Network Error' });
        } catch(e) { console.log('Dialog not available for error reporting'); }
    }
}